import { 
    memo, 
    useEffect, 
    useState, 
    MouseEventHandler, 
    useRef
} from 'react';
import { Row, Col, Card, Button } from 'react-bootstrap';
import { useFetch } from '../../services/Requests/useFetch';
import { StopTravelControlBanner } from './StopTravelControlBanner';
import { Subscriber, useMsg } from '../../services/Ros/Subscriber';
import { GPS_TOPIC } from '../../constants/topics';
import { StopTravelMap } from './StopTravelMap';
import { useRobotGPS } from '../../services/Ros/useRobotGPS';
// import { RobotState } from '../../core/Messages/RobotStateMessage';
import { useRealtimeUpdates } from '../../services/Requests/websocket';
import AssetTravelSelect from './AssetTravelSelect';
import RosRobotMap from '../../core/Maps/RosRobotMap';
import { useRobotTF } from '../../services/Ros/UseRobotTF';
import { useRobotDetails } from '../../utilities/RobotDetailsContext';
import { IndoorStopTravelControlBanner } from './IndoorStopTravelControlBanner';
import { useServiceCaller } from '../../services/Ros/ServiceCaller';
import { useConnection } from '../../services/Ros/Connection';
import { parseRobotState, RobotStateMessage } from '../DrivingControls/DockingControl';
import { useEmail } from '../../utilities/http/routing/EmailContext';
// import { ServiceCaller } from '../../services/Ros/ServiceCaller';

type LastRun = string | null | undefined;

export interface StopData {
    stop: string;
    pathname: string;
    next: string;
    next_pathname: string;
    gps: number[];
    zone: string;
}

export interface IStopInfo {
    stops: StopData[];
}

export interface IZoneInfo  {
    zones: IStopInfo[];
}

interface IRunMission {
    t: string;
}

const options: RequestInit = {
    method: "GET"
}

interface IUpdateMessage { 
    // data: string;
    id : number;
    prog : number;
    length: number;
    run : boolean;
    start_time? : LastRun;
}

export interface IMissionInfo {
    length : number;
    prog : number;
    run : boolean;
    last_run? : LastRun;
    start_time?: LastRun;
}

interface StopTravelBodyProps {
    initialActiveIndex?: string;
    initialMissionInfo?: IStopInfo;
    initialLaunched?: boolean;
    setInitialActiveIndex: (value: string) => void;
    setInitialMissionInfo: (value: IStopInfo) => void;
    setInitialLaunched: (value: boolean) => void;
}

// Haversine formula to calculate the distance between two GPS points
export interface GPSPoint {
    lat: number;
    long: number;
}

export function haversineDistance(point1: GPSPoint, point2: GPSPoint): number {
    const toRadians = (degrees: number) => degrees * (Math.PI / 180);

    const R = 6371e3; // Earth's radius in meters
    const φ1 = toRadians(point1.lat);
    const φ2 = toRadians(point2.lat);
    const Δφ = toRadians(point2.lat - point1.lat);
    const Δλ = toRadians(point2.long - point1.long);

    const a =
        Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) *
        Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const distance = R * c; // in meters

    return distance;
}

function arePointsClose(point1: GPSPoint, point2: GPSPoint, threshold: number,indoor:boolean): boolean {
    let distance: number;
    if (!indoor){
        distance = haversineDistance(point1, point2);
        
    }else{
        distance = Math.sqrt(Math.pow(point2.lat - point1.lat, 2) + Math.pow(point2.long - point1.long, 2));
        distance = distance*0.05;
    }
    return distance <= threshold;
}


function getClosestStop(robotPos: GPSPoint,stopList: IStopInfo, indoor:boolean): StopData | null{
    let returnStop = null;
    let distance=3;
    stopList.stops.forEach(stopData => {
        const gpsPoint: GPSPoint = { lat: stopData.gps[0], long: stopData.gps[1] };
        // console.log(gpsPoint)
        if (arePointsClose(robotPos,gpsPoint,2.5,indoor)){
            let checkDistance;
            if (indoor){
                checkDistance = haversineDistance(robotPos,gpsPoint)
            }else{
                checkDistance = Math.sqrt(Math.pow(robotPos.lat - gpsPoint.lat, 2) + Math.pow(robotPos.long - gpsPoint.long, 2));
                checkDistance = checkDistance*0.05;
            }
            // console.log(checkDistance)
            if (checkDistance<distance){
                distance = checkDistance;
                returnStop = stopData;
            }
        }
    });

    // console.log(distance,returnStop);
    return returnStop;
}

export function getNextStopNumber(stops: StopData[]): string {
    const numbers = stops
        .map(s => Number(s.stop))
        .filter(n => Number.isInteger(n) && n >= 0 && n <= 999);

    const highest = numbers.length ? Math.max(...numbers) : 1;

    return String(highest + 1).padStart(3, "0");
}

export const StopTravelBodyMemo = ({
    initialActiveIndex = '',
    initialMissionInfo = { stops: [] },
    initialLaunched = false,
    setInitialActiveIndex,
    setInitialMissionInfo,
    setInitialLaunched
}: StopTravelBodyProps) => {
    const {robotDetails} = useRobotDetails();
    const fetchMissionPlanData = useFetch<StopData[]>('/api/missions/get_stops_data', options);
    const runMissionPlanHome = useFetch<IRunMission>('/api/missions/stop_to_home', options);
    const runGoToPoint = useFetch<IRunMission>('/api/missions/go_to_point', options);
    const runGoToStop = useFetch<IRunMission>('/api/missions/go_to_stop', options);
    const runGoToAsset = useFetch<IRunMission>('/api/missions/go_to_asset', options);

    const { ws,lastMessage } = useRealtimeUpdates();                                                         //Get the live updates websocket from the context provider.
    const { userEmail } = useEmail();
    const socket = ws

    const [activeIndex, setActiveIndex] = useState<string>(initialActiveIndex);
    const [missionInfo, setMissionInfo] = useState<IStopInfo>(initialMissionInfo);
    const [traversalInfo, setTraversalInfo] = useState<IMissionInfo>({length: lastMessage?.data.length, prog : lastMessage?.data.prog, run : lastMessage?.data.run})
    const [activeStop, setActiveStop] = useState<StopData>();
    const [currentStop, setCurrentStop] = useState<StopData | null>();
    const [homeStop, setHomeStop] = useState<StopData>();
    const [launched, setLaunched] = useState<boolean>(initialLaunched);
    const [pointsAreClose, setPointsAreClose] = useState<boolean>(false)
    const [robotIsHome, setRobotIsHome] = useState<boolean>(true)
    const [prevRobotGPS, setPrevRobotGPS] = useState<{ lat: number, long: number } | null>(null)
    const prevRobotGPSRef = useRef(prevRobotGPS)
    const robotTF = useRobotTF()
    const robotGPS = useRobotGPS()
    // console.log(currentStop)
    const [parsedState, setParsedState] = useState<string>("UNKNOWN");
    const { callService } = useServiceCaller();
    const connection = useConnection()
    const msg = useMsg();
    
        try {
            useEffect(() => {
                const fetchRobotState = async () => {
                    const request: any = {};
    
                        const result = await callService(request);
                        if (result.success && result.message) {
                            setParsedState(parseRobotState({data: parseInt(result.message)} as RobotStateMessage))
                            console.log(result)
                        } else {
                            console.error("Service returned an unsuccessful response.");
                        }
                    
                };
                if (connection){
                    fetchRobotState();
                }
            }, [callService,connection]);
        } catch (error) {
            console.error("Service call failed:", error);
        }
    
        useEffect(() => {
            setParsedState(parseRobotState(msg as RobotStateMessage))
        }, [msg]);
    

    const handleSelected = (id: string) => {
        setActiveIndex(id);
        setInitialActiveIndex(id);
        
        // Split the id into zone and stop
        const [zone, stop] = id.split('/');
    
        // Find the matching stop data
        const selectedStop = missionInfo.stops.find((stopData) => 
            stopData.zone === zone && stopData.stop === stop
        );
    
        // If found, set the activeStop
        if (selectedStop) {
            setActiveStop(selectedStop);
        }
    
        console.log(selectedStop);
    }
    
    const handleRunHome: MouseEventHandler = (_e) => {
        if (currentStop) {
            const info = [currentStop.zone,currentStop.stop];
            runMissionPlanHome.get([
                { param: 'mission', value: 333 },
                { param: 'host', value: robotDetails.rosHostname },
                { param: 'port', value: robotDetails.rosPort },
                { param: 'stop', value: info[1] },
                { param: 'zone', value: info[0] },
                { param: 'user', value: userEmail},
                { param: 'dock', value: false},
            ]);
        }
        setLaunched(true);
        setInitialLaunched(true);
    }

    const handleIndoorHome: MouseEventHandler = (_e) => {
        const info = ["zone0","home"];
        runGoToPoint.get([
            { param: 'mission', value: 333 },
            { param: 'host', value: robotDetails.rosHostname },
            { param: 'port', value: robotDetails.rosPort },
            { param: 'stop', value: info[1] },
            { param: 'zone', value: info[0] },
            { param: 'user', value: userEmail},
            { param: 'dock', value: false},
        ]);
        setLaunched(true);
        setInitialLaunched(true);
    }

    const handleRunHomeDock: MouseEventHandler = (_e) => {
        if (currentStop) {
            const info = [currentStop.zone,currentStop.stop];
            runMissionPlanHome.get([
                { param: 'mission', value: 333 },
                { param: 'host', value: robotDetails.rosHostname },
                { param: 'port', value: robotDetails.rosPort },
                { param: 'stop', value: info[1] },
                { param: 'zone', value: info[0] },
                { param: 'user', value: userEmail},
                { param: 'dock', value: true},
            ]);
        }
        setLaunched(true);
        setInitialLaunched(true);
    }

    const handleIndoorHomeDock: MouseEventHandler = (_e) => {
        const info = ["zone0","home"];
        runGoToPoint.get([
            { param: 'mission', value: 333 },
            { param: 'host', value: robotDetails.rosHostname },
            { param: 'port', value: robotDetails.rosPort },
            { param: 'stop', value: info[1] },
            { param: 'zone', value: info[0] },
            { param: 'user', value: userEmail},
            { param: 'dock', value: true},
        ]);
        setLaunched(true);
        setInitialLaunched(true);
    }

    const handleIndoorStop: MouseEventHandler = (_e) => {
        if (activeIndex !== ''){
            const info =  activeIndex.split('/');
            runGoToPoint.get([
                { param: 'mission', value: 333 },
                { param: 'host', value: robotDetails.rosHostname },
                { param: 'port', value: robotDetails.rosPort },
                { param: 'stop', value: info[1] },
                { param: 'zone', value: info[0] },
                { param: 'user', value: userEmail},
            ]);
            setLaunched(true);
            setInitialLaunched(true);
        }
    }

    const handleRunStop: MouseEventHandler = (_e) => {
        if (activeIndex !== ''&& currentStop) {
            const info = activeIndex.split('/');
            const zones = `${currentStop.zone},${info[0]}`
            const stops = `${currentStop.stop},${info[1]}`
            // const zones = `zone2,${info[0]}`
            // const stops = `stop3,${info[1]}`
            runGoToStop.get([
                { param: 'mission', value: 333 },
                { param: 'host', value: robotDetails.rosHostname },
                { param: 'port', value: robotDetails.rosPort },
                { param: 'stop', value: stops },
                { param: 'zone', value: zones },
                { param: 'user', value: userEmail},
            ]);
        }
        setLaunched(true);
        setInitialLaunched(true);
    }

    const handleRunAsset = (
        zone: string,
        stop: string,
        asset: string
      ): void => {
        console.log(zone && stop && asset && currentStop);
        if (zone && stop && asset && (currentStop || robotDetails.isIndoor || robotDetails.simpleRec)) {
            if (robotDetails.isIndoor || robotDetails.simpleRec){
                let zones  = `"zone0","zone1"`;
                let stops = `"home",${stop}`;
                if (currentStop){
                    zones = `${currentStop.zone},${zone}`;
                    stops = `${currentStop.stop},${stop}`;
                }
                
            
                runGoToAsset.get([
                    { param: 'mission', value: 333 },
                    { param: 'host', value: robotDetails.rosHostname },
                    { param: 'port', value: robotDetails.rosPort },
                    { param: 'stop', value: stops },
                    { param: 'zone', value: zones },
                    { param: 'asset', value: asset },
                    { param: 'user', value: userEmail},
                ]);
            }else if (currentStop){
                const zones = `${currentStop.zone},${zone}`;
                const stops = `${currentStop.stop},${stop}`;
            
                runGoToAsset.get([
                    { param: 'mission', value: 333 },
                    { param: 'host', value: robotDetails.rosHostname },
                    { param: 'port', value: robotDetails.rosPort },
                    { param: 'stop', value: stops },
                    { param: 'zone', value: zones },
                    { param: 'asset', value: asset },
                    { param: 'user', value: userEmail},
                ]);
            }
          setLaunched(true);
          setInitialLaunched(true);

        } else {
          console.error('Missing required parameters: zone, stop, or asset.');
        }
    };

    useEffect(() => {
        prevRobotGPSRef.current = prevRobotGPS
    }, [prevRobotGPS])
    
    useEffect(() => {
        fetchMissionPlanData.get();

        return () => {
            fetchMissionPlanData.abortRequest && fetchMissionPlanData.abortRequest();
        }
    }, []);

    useEffect(() => {
        const { state: { status, data } } = fetchMissionPlanData;

        if (status === 'fetched' && data && data.length > 0) {
            console.log(data)
            console.log(data[0])
            setHomeStop(data[0])
            setMissionInfo({ stops: data });
            setInitialMissionInfo({ stops: data });
        }
    }, [fetchMissionPlanData.state]);

    useEffect(() => {
        setActiveIndex(initialActiveIndex);
        setMissionInfo(initialMissionInfo);
        // setLaunched(initialLaunched);
    }, [initialActiveIndex, initialMissionInfo, initialLaunched]);

    const handleMarkerClick = (e: StopData) => {
        setActiveStop(e);
        setActiveIndex(`${e.zone}/${e.stop}`);
        setInitialActiveIndex(`${e.zone}/${e.stop}`);
    }

    const areGPSPointsEqual = (gps1: GPSPoint | null, gps2: GPSPoint | null): boolean => {
        if (!gps1 || !gps2) return false;
        return gps1.lat === gps2.lat && gps1.long === gps2.long;
    };

    useEffect(() => {
        if (!robotDetails.isIndoor){
            if (!areGPSPointsEqual(robotGPS, prevRobotGPSRef.current)){
                if (robotGPS && missionInfo){
                    const robotPos: GPSPoint = { lat: robotGPS.lat, long: robotGPS.long }; // Example GPS point
                    setCurrentStop(getClosestStop(robotPos,missionInfo,robotDetails.isIndoor))
                }

                if (robotGPS && currentStop) {
                    const thresholdDistance = 2.5; // in meters
                    const gpsPoint1: GPSPoint = { lat: currentStop.gps[0], long: currentStop.gps[1] }; // Example GPS point
                    const gpsPoint2: GPSPoint = { lat: robotGPS.lat, long: robotGPS.long }; // Example GPS point
                    setPointsAreClose(arePointsClose(gpsPoint1, gpsPoint2, thresholdDistance,robotDetails.isIndoor));
                }
                
                // if (robotGPS && activeStop) {
                //     const thresholdDistance = 2.5; // in meters
                //     const gpsPoint1: GPSPoint = { lat: activeStop.gps[0], long: activeStop.gps[1] }; // Example GPS point
                //     const gpsPoint2: GPSPoint = { lat: robotGPS.lat, long: robotGPS.long }; // Example GPS point
                //     // setPointsAreClose(arePointsClose(gpsPoint1, gpsPoint2, thresholdDistance));
                // }

                if (robotGPS && homeStop){
                    const thresholdDistance = 2.5;
                    const gpsPoint1: GPSPoint = { lat: homeStop.gps[0], long: homeStop.gps[1] }; // Example GPS point
                    const gpsPoint2: GPSPoint = { lat: robotGPS.lat, long: robotGPS.long }; // Example GPS point
                    setRobotIsHome(arePointsClose(gpsPoint1, gpsPoint2, thresholdDistance,robotDetails.isIndoor));
                }
                setPrevRobotGPS(robotGPS)
            }
        }else{
            if (robotTF && missionInfo){
                const robotPos: GPSPoint = { lat: robotTF.lat, long: robotTF.long }; // Example GPS point
                // console.log(robotTF)
                setCurrentStop(getClosestStop(robotPos,missionInfo,robotDetails.isIndoor))
            }

            if (robotTF && currentStop) {
                const thresholdDistance = 2.5; // in meters
                const gpsPoint1: GPSPoint = { lat: currentStop.gps[0], long: currentStop.gps[1] }; // Example GPS point
                const gpsPoint2: GPSPoint = { lat: robotTF.lat, long: robotTF.long }; // Example GPS point
                setPointsAreClose(arePointsClose(gpsPoint1, gpsPoint2, thresholdDistance,robotDetails.isIndoor));
            }

            if (robotTF && homeStop){
                const thresholdDistance = 2.5;
                const gpsPoint1: GPSPoint = { lat: homeStop.gps[0], long: homeStop.gps[1] }; // Example GPS point
                const gpsPoint2: GPSPoint = { lat: robotTF.lat, long: robotTF.long }; // Example GPS point
                setRobotIsHome(arePointsClose(gpsPoint1, gpsPoint2, thresholdDistance,robotDetails.isIndoor));
            }
            setPrevRobotGPS(robotGPS)
        }

    }, [robotGPS,robotTF, activeStop]);

     /**
     * Receives live update messages from the NOTIFY function on the postgres database for mission plans.
     * Listens for a message containing id of the mission plan, current progress, and whether the mission is still running.
     * @param message The message received by the websocket
     */
     const onSocketMessage = (message : MessageEvent) => {
        const data : IUpdateMessage = JSON.parse(message.data);
        console.log(message.data)
        setLaunched(data.run)
        // if(data.id === activeIndex){
        setTraversalInfo({...traversalInfo, prog : data.prog, run : data.run, length: data.length,start_time:data.start_time});
        console.log(traversalInfo)
        // }
    };
    useEffect(()=>{
        if (lastMessage){
            onSocketMessage(lastMessage);
            console.log(` Last Message:${lastMessage.data}`)
        }
      
    },[lastMessage]);

    useEffect(() => {
        if (traversalInfo.run !== true ) {
            console.log("Launch false")
            setLaunched(false);
            setInitialLaunched(false);
        }
        else {
            setLaunched(true);
            setInitialLaunched(true);
        }

    }, [traversalInfo.run]);

    /**
     * Runs when the notification websocket, active index or missionInfo object are changed.
     * Responsible for adding a websocket message listener to listen for the live updates from the database.
     */
    useEffect(() => {

        socket.addEventListener('message', onSocketMessage);

        //Cleanup. Removes listener when this component is destroyed.
        return () => {
            socket.removeEventListener('message', onSocketMessage);
        }

    }, [socket, activeIndex, missionInfo]);

    return (
        <Row className='h-100'>
            <Col lg={9} className="ps-0">
            {!robotDetails.isIndoor && <Subscriber name={GPS_TOPIC.name} type={GPS_TOPIC.type} rate={GPS_TOPIC.throttle}>
                    <StopTravelMap missionInfo={missionInfo} onMarkerClick={handleMarkerClick} activeIndex={activeIndex} />
                </Subscriber>}
                {robotDetails.isIndoor && <RosRobotMap markerList={missionInfo} handleMarker={handleMarkerClick} buttons={true}/>}
                {/* <Subscriber name={STATE.name} type={STATE.type} rate={STATE.throttle}>
                    <ServiceCaller name="/state_request_service" type="std_srvs/srv/Trigger">   
                        <RobotState name="robot_state" onStateChange={handleRobotStateChange} />
                    </ServiceCaller>
                </Subscriber> */}
            </Col>
            <Col lg={3} className="mt-3 mb-3 path-planning-column">
                {!(robotDetails.isIndoor || robotDetails.simpleRec) ? <StopTravelControlBanner
                    currentStop={currentStop} 
                    onSelect={handleSelected} 
                    onRun={handleRunHome}
                    onRunStop={handleRunStop}
                    selectedValue={activeIndex}
                    running={launched}
                    safe={pointsAreClose}
                    home={robotIsHome}    
                    progress={traversalInfo.prog}
                    robotState={parsedState}       
                />
                :
                <IndoorStopTravelControlBanner
                    currentStop={currentStop} 
                    onSelect={handleSelected} 
                    onRun={handleIndoorHome}
                    onRunStop={handleIndoorStop}
                    selectedValue={activeIndex}
                    running={launched}
                    safe={pointsAreClose}
                    home={robotIsHome}    
                    progress={traversalInfo.prog}
                    robotState={parsedState}          
                />}
                <Card className="mb-3">
                    {!(robotDetails.isIndoor || robotDetails.simpleRec) ? <Button variant="primary" onClick={handleRunHomeDock} disabled={parsedState !== "READY" || launched || robotIsHome} className="flex-fill me-2">
                        Go Home & Dock
                    </Button>
                    :
                    <Button variant="primary" onClick={handleIndoorHomeDock} disabled={parsedState !== "READY" || launched || robotIsHome} className="flex-fill me-2">
                        Go Home & Dock
                    </Button>}
                </Card>
                {true ? (
                        <div className="mt-3">
                            <h5>Asset Imager</h5>
                            <Card className="mb-3">
                                <Card.Body>
                                    <AssetTravelSelect handleRunAsset={handleRunAsset} Running={launched|| parsedState !== "READY" }/>
                                </Card.Body>
                            </Card>
                        </div>
                    ) : null}
            </Col>
        </Row>

    );
}

export const StopTravelBody = memo(StopTravelBodyMemo);
